import { useState, useEffect } from 'react';
import _ from 'lodash';
import LabelValuePair from '../Common/LabelValuePair';
import SectionHeader from '../Common/SectionHeader';
import ToolTip from '../Common/Tooltip';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { REMOVE_DETACHED_SENSOR, SET_ONGOING_EDIT_GLOBALLY, UPDATE_SENSOR_INFORMATION } from '../../../store/actions';
import { useQuery } from 'react-query';
import { detachSensorById, updateSensorById } from '../../../api';
import { checkForAPISuccess, responseToastHandler, showErrorToast, showSuccessToast } from '../../../Common/commonFunctions';
import { ClipLoader } from 'react-spinners';
import { Button, Modal } from 'react-bootstrap';
import { RESPONSE_LOCATION_BLOCKED_CODE } from '../../../Common/constants';

const SensorDetail = props => {
  const EDIT = 'EDIT',
    CANCEL = 'CANCEL',
    SAVE = 'SAVE',
    DETACH = 'DETACH'

	const selectedComponent = useSelector(state => state.equipment.selectedComponent);

    const [utils, setUtils] = useState({
        metadataInitial: {}, isSensorDetachActive: false,
        metadata: {}, sensorInEdit: "" 
    });
    
    const useQueryConfiguration = {
        enabled: false, retry: false, retryOnMount: false,
        refetchOnMount: false, refetchOnWindowFocus: false
    };

    const dispatch = useDispatch();
    const isEditOngoing = useSelector(state => state.equipment.isEditOngoing);
    const isLockedProfile = useSelector(state => state.equipment.isLockedProfile);
    
	const hasMetaDataChanged = () => !_.isEqual(utils.metadata.axial, utils.metadataInitial.axial) || !_.isEqual(utils.metadata.vertical, utils.metadataInitial.vertical);

	const hasInstallLocationChanged = () => !_.isEqual(utils.metadata.location, utils.metadataInitial.location);

	const updateSensor = async () => {
		let payload = {};
		if (hasMetaDataChanged()) {
			payload.metadata = {
				install_info: {
					axial: utils?.metadata?.axial,
					vertical: utils?.metadata?.vertical,
				},
			};
		}
		if (hasInstallLocationChanged()) {
			payload.installLocation = utils.metadata?.location;
		}
		const response = await updateSensorById(props.id, payload);
		return response?.data;
	};

    const detachSensor = async () => {
        const response  = await detachSensorById(props.id);
        return response?.data;
    }

    const updateSensorQuery = useQuery("update-sensor-information", updateSensor, { ...useQueryConfiguration });

    const detachSensorQuery = useQuery("detach-sensor", detachSensor, { ...useQueryConfiguration });

	useEffect(() => {
		if (_.isEqual(props.id, utils.sensorInEdit) && updateSensorQuery.isFetched) {
			const data = updateSensorQuery?.data;
			const statusCode = data?.statusCode;
			if (
				updateSensorQuery.isSuccess &&
				(checkForAPISuccess(statusCode) || _.isEqual(statusCode, RESPONSE_LOCATION_BLOCKED_CODE))
			) {
				if (statusCode === RESPONSE_LOCATION_BLOCKED_CODE) {
					setUtils({ ...utils, sensorInEdit: "", metadata: _.cloneDeep(utils.metadataInitial) });
					showErrorToast(data.statusMessage, 4000);
					dispatch({ type: SET_ONGOING_EDIT_GLOBALLY, payload: false });
				}
				else if (updateSensorQuery.isSuccess && checkForAPISuccess(data?.statusCode)) {
					dispatch({
						type: UPDATE_SENSOR_INFORMATION,
						payload: {
							id: props.id,
							metadata: { axial: utils?.metadata?.axial, vertical: utils?.metadata?.vertical },
							location: utils?.metadata?.location,
							bearingInformation: data?.data?.bearingInformation
						},
					});
					showSuccessToast('Sensor information updated successfully');
				}
				else {
					setUtils({ ...utils, metadata: _.clone(utils.metadataInitial), sensorInEdit: '' });
					dispatch({ type: SET_ONGOING_EDIT_GLOBALLY, payload: false });
					showErrorToast('Failed to update sensor information');
				}
			}
			else {
				showErrorToast('Failed to update sensor information');
			}
			updateSensorQuery.remove();
		}
	}, [updateSensorQuery.isFetched]);

	useEffect(() => {
		if (utils.isSensorDetachActive && detachSensorQuery.isFetched) {
			if (detachSensorQuery.isSuccess && checkForAPISuccess(detachSensorQuery?.data?.statusCode)) {
				showSuccessToast("Sensor detached successfully");
				dispatch({ type: REMOVE_DETACHED_SENSOR, payload: props.id });
				setUtils({ ...utils, isSensorDetachActive: false });
			}
			else {
				showErrorToast("Failed to detach sensor");
			}

			detachSensorQuery.remove();
		}
	}, [detachSensorQuery.isFetched]);

	useEffect(() => {
		if(!_.isEmpty(props?.metadata) && !_.isEqual(props.metadata, utils.metadataInitial)) {
			setUtils({
				metadataInitial: _.cloneDeep(props?.metadata), sensorInEdit: "",
				metadata: _.cloneDeep(props?.metadata), isSensorDetachActive: false
			});
		}
	}, [props.metadata]);

	const handleHeaderIcons = action => {
		switch(action) {

			case EDIT:
				setUtils({ ...utils, sensorInEdit: props.id });
				dispatch({ type: SET_ONGOING_EDIT_GLOBALLY, payload: true });
			break;

			case CANCEL:
				setUtils({ ...utils, sensorInEdit: '', metadata: _.cloneDeep(utils.metadataInitial) });
				dispatch({ type: SET_ONGOING_EDIT_GLOBALLY, payload: false });
			break;

			case SAVE:
				updateSensorQuery.refetch();
			break;

			case DETACH:
				setUtils({ ...utils, isSensorDetachActive: true });
			break;

			default:
			break;
		}
	};

    const returnToolTip = (key, value, icon, action, className, color) =>
        <ToolTip
            toolTipKey={key} toolTipValue={value}
            overlayComponent={
                <Icon className={"mr-3 cursor-pointer " + className} 
                    width={15} height={15} color={color} icon={icon}
                    onClick={() => handleHeaderIcons(action)}
                />
            }
        />

    const returnField = (label, data) =>
        <LabelValuePair
            label={label} value={utils.metadata[label.toLowerCase()]} viewMode={_.isEmpty(utils.sensorInEdit)} data={!_.isEmpty(data) && data}
            handleChange={(value) => setUtils({ ...utils, metadata: _.set(utils.metadata, label, value) })}
        />

    return (
        <>
            <SectionHeader
                headerComponent={
                    <>
                        {
                            !isLockedProfile &&
                            (
                                _.isEmpty(utils.sensorInEdit) ?
                                    <>
                                        { returnToolTip("Edit-sensor", "Edit", "feather-edit", EDIT, `${ isEditOngoing && "disable-click text-color-grey" }`) }
                                        { returnToolTip("Detach", "Detach", "feather-minus-circle", DETACH, `${ isEditOngoing && "disable-click text-color-grey" }`) }
                                    </>
                                        :
                                    <>
                                        { returnToolTip("Cancel-Sensor", "Cancel", "feather-x", CANCEL, "","text-color-grey") }
                                        { !_.isEqual(utils.metadata, utils.metadataInitial) &&
                                            returnToolTip("Save-Sensor", "Save", "feather-save", SAVE, "", "text-primary") }
                                    </>
                            )
                        }
                    </>
                }
                title={"Sensor ID: " + (props.id)}
            />
            { returnField("axial") }
            { returnField("vertical") }
            { returnField("location", !_.isEmpty(props.availableLocations) && props.availableLocations ) }

            <Modal backdrop="static" onHide={() => setUtils({ ...utils, isSensorDetachActive: false })} show={utils.isSensorDetachActive}>

                <Modal.Header closeButton>
                    <h4 className="font-weight-bold">Sensor Detach</h4>
                </Modal.Header>

                <Modal.Body>
                {
                    <div className="my-5 text-center font-weight-bold">
                        {
                            detachSensorQuery.isFetching ?
                                <ClipLoader color="#075978" />
                                    :
                                `Detach sensor ${props.id} from ${selectedComponent?.name}? `
                        }
                    </div>
                }   
                </Modal.Body>

                <Modal.Footer>
                    <Button disabled={detachSensorQuery.isFetching} onClick={() => setUtils({ ...utils, isSensorDetachActive: false })} className="secondary-button">No</Button>
                    <Button disabled={detachSensorQuery.isFetching} onClick={() => detachSensorQuery.refetch()}>Yes</Button>
                </Modal.Footer>

            </Modal>
        </>
    );
}

export default SensorDetail;
